// React
import { ReactElement, useEffect, useRef, useState } from 'react';
// Next
import { useRouter } from 'next/router';
// Types
import { Slice } from '@utmb/types/slice-handler';
import { SliceRaceProfileMapTableV2View } from '@utmb/types/index';
import { UnitEnum } from '@utmb/types/unit-enum';
// Contexts
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { useConsentCategory } from '@utmb/contexts/ConsentContext';
import { CookieCategorieEnum } from '@utmb/design-system/types/cookie-consent-helper';
// Styles
import styles from './race-track-v2.module.scss';
import { useGlobal } from '@utmb/contexts/GlobalContext';

// Overridable for local development (point it to a `pnpm dev` instance of
// the utmb-track project to test the iframe messaging end-to-end).
const TRACK_UTMB_BASE_URL = process.env.NEXT_PUBLIC_TRACK_UTMB_BASE_URL ?? 'https://track.utmb.world';

export const RaceTrackV2 = (props: Slice<SliceRaceProfileMapTableV2View>): ReactElement => {
    const { id, trackRaceId, year } = props;

    const { locale } = useRouter();

    const {
        global: {
            event: { tenantUtmbLive },
        },
    } = useGlobal();
    const {
        preferences: { preferredUnit },
    } = usePreference();
    // GDPR: the embedded widget only loads its analytics (Microsoft Clarity)
    // when told the visitor accepted "Performance cookies" here.
    const { isConsented: hasPerformanceConsent } = useConsentCategory(CookieCategorieEnum.PERFORMANCE);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const overlayRef = useRef<HTMLIFrameElement>(null);
    // Content height reported by the embedded app: UTMB Track posts
    // `{ type: 'utmb-track:height', height }` on every layout change (card
    // unfolding, window resize…) so the iframe always shows the full content
    // without an inner scrollbar. The CSS height stays as the fallback until
    // the first message arrives.
    const [height, setHeight] = useState<number | null>(null);
    // True while the companion /dialog iframe has an open dialog to show.
    const [overlayVisible, setOverlayVisible] = useState(false);

    useEffect(() => {
        // Modal semantics while a dialog is up: freeze the page scroll, and
        // compensate the vanished scrollbar so the layout doesn't shift.
        // The lock goes on BODY, not on <html>: hiding the root's overflow
        // un-sticks `position: sticky` elements (the top nav would jump to
        // its in-flow position, off-screen), while body overflow propagates
        // the lock to the viewport with sticky behavior and scroll position
        // both preserved.
        const lockScroll = (locked: boolean): void => {
            const { body } = document;
            if (locked) {
                body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
                body.style.overflow = 'hidden';
            } else {
                body.style.paddingRight = '';
                body.style.overflow = '';
            }
        };
        const onMessage = (event: MessageEvent): void => {
            if (event.origin !== TRACK_UTMB_BASE_URL) return;
            if (event.data?.type === 'utmb-track:height') {
                if (event.source !== iframeRef.current?.contentWindow) return;
                const next = Number(event.data.height);
                if (Number.isFinite(next) && next > 0) setHeight(next);
            } else if (event.data?.type === 'utmb-track:overlay') {
                // Sent by the overlay iframe: reveal it while it has an open
                // dialog — the widget's dialogs render there so their veil
                // covers the whole page instead of just the widget area.
                if (event.source !== overlayRef.current?.contentWindow) return;
                const visible = event.data.visible === true;
                setOverlayVisible(visible);
                lockScroll(visible);
            }
        };
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
            lockScroll(false);
        };
    }, []);

    if (!trackRaceId || !year || !tenantUtmbLive) return <></>;

    const searchParams = new URLSearchParams();
    if (preferredUnit === UnitEnum.IMPERIAL) searchParams.set('unit', 'imperial');
    if (hasPerformanceConsent) searchParams.set('consent', '1');
    const trackSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const src = `${TRACK_UTMB_BASE_URL}/${locale}/${tenantUtmbLive}/${year}/${trackRaceId}/track${trackSearch}`;
    // The overlay iframe never gets `consent`: it runs the same app, and
    // loading Clarity there too would record a second, empty session.
    const unitParam = preferredUnit === UnitEnum.IMPERIAL ? '?unit=imperial' : '';
    const overlaySrc = `${TRACK_UTMB_BASE_URL}/${locale}/${tenantUtmbLive}/${year}/${trackRaceId}/dialog${unitParam}`;

    return (
        <section id={id} className={styles.container}>
            <iframe
                ref={iframeRef}
                className={styles.iframe}
                src={src}
                title="Race Profile / Map / Table"
                allowFullScreen={true}
                frameBorder={0}
                scrolling="no"
                style={height ? { height: `${height}px` } : undefined}
            />
            {/* Companion overlay: same app, /dialog route — transparent,
                fixed over the whole viewport, revealed only while the widget
                has an open dialog. Kept mounted (visibility, not display) so
                the app inside stays warm. */}
            <iframe
                ref={overlayRef}
                src={overlaySrc}
                title="Race dialogs"
                frameBorder={0}
                style={{
                    position: 'fixed',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                    zIndex: 2147483000,
                    visibility: overlayVisible ? 'visible' : 'hidden',
                    pointerEvents: overlayVisible ? 'auto' : 'none',
                }}
            />
        </section>
    );
};
